/** 5-row bitmap fonts for the contribution-grid word. */
const GLYPHS: Readonly<Record<string, readonly string[]>> = {
  A: ["01110", "10001", "11111", "10001", "10001"],
  B: ["11110", "10001", "11110", "10001", "11110"],
  C: ["01111", "10000", "10000", "10000", "01111"],
  D: ["11110", "10001", "10001", "10001", "11110"],
  E: ["11111", "10000", "11110", "10000", "11111"],
  F: ["11111", "10000", "11110", "10000", "10000"],
  G: ["01111", "10000", "10011", "10001", "01111"],
  H: ["10001", "10001", "11111", "10001", "10001"],
  I: ["111", "010", "010", "010", "111"],
  J: ["00111", "00010", "00010", "10010", "01100"],
  K: ["10001", "10010", "11100", "10010", "10001"],
  L: ["10000", "10000", "10000", "10000", "11111"],
  M: ["10001", "11011", "10101", "10001", "10001"],
  N: ["10001", "11001", "10101", "10011", "10001"],
  O: ["01110", "10001", "10001", "10001", "01110"],
  P: ["11110", "10001", "11110", "10000", "10000"],
  Q: ["01110", "10001", "10101", "10010", "01101"],
  R: ["11110", "10001", "11110", "10010", "10001"],
  S: ["11111", "10000", "11111", "00001", "11111"],
  T: ["11111", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "01110"],
  V: ["10001", "10001", "10001", "01010", "00100"],
  W: ["10001", "10001", "10101", "11011", "10001"],
  X: ["10001", "01010", "00100", "01010", "10001"],
  Y: ["10001", "01010", "00100", "00100", "00100"],
  Z: ["11111", "00010", "00100", "01000", "11111"],
  " ": ["00", "00", "00", "00", "00"],
};

const WEEK_COUNT: number = 52;
const ROW_COUNT: number = 7;
const LEVEL_COLORS: readonly [string, string, string, string, string] = [
  "rgba(255,255,255,0.07)",
  "#1D473A",
  "#2F7058",
  "#47A382",
  "#6FE3B4",
];

export type HeatmapTwinkle = "none" | "back" | "fore";

export interface HeatmapCell {
  readonly id: string;
  readonly color: string;
  readonly twinkle: HeatmapTwinkle;
  readonly duration: string;
  readonly delay: string;
}

export interface HeatmapWeek {
  readonly id: string;
  readonly cells: readonly HeatmapCell[];
}

export interface ShipHeatmapModel {
  readonly weeks: readonly HeatmapWeek[];
  readonly contribCount: string;
}

function glyphWidth(glyph: readonly string[]): number {
  const first: string | undefined = glyph[0];
  return first === undefined ? 0 : first.length;
}

function wordWidth(
  glyphs: readonly (readonly string[])[],
  scale: number,
  gap: number,
): number {
  if (glyphs.length === 0) return 0;
  const bars: number = glyphs.reduce(
    (sum: number, glyph: readonly string[]) => sum + glyphWidth(glyph) * scale,
    0,
  );
  return bars + (glyphs.length - 1) * gap;
}

function seedFromWord(word: string): number {
  let seed: number;
  seed = 2661;
  for (const ch of word) {
    seed = (Math.imul(seed, 31) + ch.charCodeAt(0)) >>> 0;
  }
  return seed;
}

function nextUnit(state: { readonly t: number }): {
  readonly t: number;
  readonly unit: number;
} {
  const t: number = (state.t + 0x6d2b79f5) >>> 0;
  let r: number = Math.imul(t ^ (t >>> 15), 1 | t);
  r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
  return { t, unit: ((r ^ (r >>> 14)) >>> 0) / 4294967296 };
}

function paintMask(
  glyphs: readonly (readonly string[])[],
  scale: number,
  gap: number,
): boolean[][] {
  const mask: boolean[][] = Array.from({ length: ROW_COUNT }, () =>
    Array.from({ length: WEEK_COUNT }, () => false),
  );
  const total: number = wordWidth(glyphs, scale, gap);
  let x: number = Math.max(1, Math.floor((WEEK_COUNT - total) / 2));
  for (const glyph of glyphs) {
    const gw: number = glyphWidth(glyph);
    for (let r = 0; r < 5; r += 1) {
      const row: string | undefined = glyph[r];
      if (row === undefined) continue;
      for (let c = 0; c < gw; c += 1) {
        if (row[c] !== "1") continue;
        for (let s = 0; s < scale; s += 1) {
          const col: number = x + c * scale + s;
          const maskRow: boolean[] | undefined = mask[r + 1];
          if (maskRow !== undefined && col < WEEK_COUNT) maskRow[col] = true;
        }
      }
    }
    x += gw * scale + gap;
  }
  return mask;
}

/** GitHub-style year grid with `word` painted in the bright cells. */
export function buildShipHeatmap(
  word: string,
  anim: boolean,
): ShipHeatmapModel {
  const upper: string = word.toUpperCase();
  const glyphs: (readonly string[])[] = Array.from(upper)
    .map((ch: string) => GLYPHS[ch])
    .filter(
      (glyph: readonly string[] | undefined): glyph is readonly string[] =>
        glyph !== undefined,
    );
  let scale: number;
  scale = 2;
  let gap: number;
  gap = 2;
  if (wordWidth(glyphs, 2, 2) > 50) {
    scale = 1;
    gap = 1;
  }
  while (glyphs.length > 0 && wordWidth(glyphs, scale, gap) > 50) {
    glyphs.pop();
  }
  const mask: boolean[][] = paintMask(glyphs, scale, gap);
  let rng: { readonly t: number } = { t: seedFromWord(upper) };
  let sum: number;
  sum = 0;
  const weeks: HeatmapWeek[] = Array.from({ length: WEEK_COUNT }, (_, i) => {
    const cells: HeatmapCell[] = Array.from({ length: ROW_COUNT }, (_, r) => {
      const on: boolean = mask[r]?.[i] === true;
      let level: number;
      if (on) {
        level = 4;
      } else {
        const roll: ReturnType<typeof nextUnit> = nextUnit(rng);
        rng = { t: roll.t };
        level = roll.unit < 0.55 ? 0 : roll.unit < 0.92 ? 1 : 2;
      }
      sum += level;
      let twinkle: HeatmapTwinkle = "none";
      let duration: string;
      duration = "0s";
      let delay: string;
      delay = "0s";
      if (anim && level > 0) {
        const chance: ReturnType<typeof nextUnit> = nextUnit(rng);
        rng = { t: chance.t };
        const threshold: number = level === 4 ? 0.38 : 0.5;
        if (chance.unit < threshold) {
          const durRoll: ReturnType<typeof nextUnit> = nextUnit(rng);
          rng = { t: durRoll.t };
          const delayRoll: ReturnType<typeof nextUnit> = nextUnit(rng);
          rng = { t: delayRoll.t };
          twinkle = level === 4 ? "fore" : "back";
          duration = `${(2.6 + durRoll.unit * 2.2).toFixed(2)}s`;
          delay = `${(-delayRoll.unit * 5).toFixed(2)}s`;
        }
      }
      return {
        id: `w${String(i)}-r${String(r)}`,
        color: LEVEL_COLORS[level] ?? LEVEL_COLORS[0],
        twinkle,
        duration,
        delay,
      };
    });
    return { id: `w${String(i)}`, cells };
  });
  return {
    weeks,
    contribCount: (sum * 2).toLocaleString("en-US"),
  };
}

export const SHIP_HEATMAP: ShipHeatmapModel = buildShipHeatmap("SHIP", true);

export const HEATMAP_MONTHS: readonly string[] = [
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
];
